import { Modal, Col, Row, Image, Spin } from 'antd';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChildrensAction } from '../../../../utils';
import { MyButton, MyText } from '../../../common';
import { CalendarOutlined, UserOutlined, ManOutlined, WomanOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

interface ChildSelectModalProps {
  visible: boolean;
  selectedId: number | null;
  onSelect: (childId: number) => void;
  onConfirm: () => void;
  minAgeMonths?: any; 
  maxAgeMonths?: any;
}

export const ChildSelectModal: React.FC<ChildSelectModalProps> = ({
  visible,
  selectedId,
  onSelect,
  onConfirm,
  minAgeMonths,
  maxAgeMonths,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const childrensList = useSelector((state: any) => state.materials.childrens);
  const allChildrens = childrensList?.data?.children || [];
  const loading = childrensList?.loading;

  const childrens = allChildrens.filter((child: any) => {
    if (minAgeMonths != null && child.age_in_months < minAgeMonths) return false;
    if (maxAgeMonths != null && child.age_in_months > maxAgeMonths) return false;
    return true;
  });

  useEffect(() => {
    if (visible) {
      dispatch(fetchChildrensAction());
    }
  }, [visible]);

  const getYearLabel = (age: any) => { const lastDigit = age % 10; const lastTwoDigits = age % 100; if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return t("ages2"); if (lastDigit === 1) return t("ages1"); if (lastDigit >= 2 && lastDigit <= 4) return t("ages"); return t("ages2"); };
  const getMonthLabel = (age: any) => { const lastDigit = age % 10; const lastTwoDigits = age % 100; if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return t("months"); if (lastDigit === 1) return t("months2"); if (lastDigit >= 2 && lastDigit <= 4) return t("months1"); return t("months"); };

  const formatAge = (ageInMonths: number) => {
    const years = Math.floor(ageInMonths / 12);
    const months = ageInMonths % 12;
    let result = '';
    if (years > 0) result += `${years} ${getYearLabel(years)}`;
    if (months > 0) result += (result ? ' ' : '') + `${months} ${getMonthLabel(months)}`;
    return result || t('months');
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      centered
      width={560}
    >
      <Col span={24} style={{ marginTop: 22, marginBottom: 16 }}>
        <MyText color='#0A033C' size={28} weight={800} style={{ fontFamily: 'Nunito' }}>
          {t('chooseChild')}
        </MyText>
      </Col>

      <Spin spinning={loading}>
        <Row gutter={[0, 12]}>
          {childrens.map((child: any) => (
            <Col
              key={child.id}
              span={24}
              style={{
                border: selectedId === child.id ? '2px solid #1890ff' : '2px solid transparent',
                padding: 12,
                borderRadius: 16,
                cursor: 'pointer',
              }}
              onClick={() => onSelect(child.id)}
            >
              <Row align='middle' gutter={8}>
                <Image
                  src={child?.avatar}
                  preview={false}
                  width={60}
                  height={60}
                  style={{ marginRight: 12 }}
                />
                <Col>
                  <MyText color='rgba(10, 3, 60, 1)' size={22} weight={600} style={{ fontFamily: 'Nunito', display: 'block' }}>
                    {child.first_name}
                  </MyText>
                  <Row gutter={8}>
                    <Col>
                      <MyText color='rgba(10, 3, 60, 1)' size={14} weight={600} style={{ fontFamily: 'Nunito', display: 'block', marginTop: 6 }}>
                        <CalendarOutlined style={{ marginRight: 6 }} />
                        {dayjs(child.birth_date).format('DD.MM.YYYY')}
                      </MyText>
                    </Col>
                    <Col>
                      <MyText color='rgba(10, 3, 60, 1)' size={14} weight={600} style={{ fontFamily: 'Nunito', display: 'block', marginTop: 6 }}>
                        <UserOutlined style={{ marginRight: 6 }} />
                        ({formatAge(child.age_in_months)})
                      </MyText>
                    </Col>
                    <Col>
                      <MyText color='rgba(10, 3, 60, 1)' size={14} weight={600} style={{ fontFamily: 'Nunito', display: 'block', marginTop: 6 }}>
                        {Number(child.gender) === 1
                          ? <ManOutlined style={{ marginRight: 6 }} />
                          : <WomanOutlined style={{ marginRight: 6 }} />}
                        {Number(child.gender) === 1 ? t('boy') : t('girl')}
                      </MyText>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          ))}
        </Row>
      </Spin>

      <Row justify='center' style={{ marginTop: 24, marginBottom: 8 }}>
        <MyButton
          color='orange'
          type='primary'
          disabled={!selectedId}
          onClick={onConfirm}
        >
          {t('continue')}
        </MyButton>
      </Row>
    </Modal>
  );
};